/**
 * GetStartedChecklist Component
 *
 * Fixed-position widget that tracks real user onboarding actions.
 * Shows at bottom-right of screen when triggered and not completed.
 *
 * Two in-place sizes plus a dismiss:
 *  - expanded  — header + steps (default)
 *  - collapsed — a 52px checklist-icon circle stacked directly above the OptiBot
 *                FAB (no bigger than it), with a badge counting the steps left.
 *                Clicking it expands the panel and opens the next step. It
 *                deliberately stays bottom-right rather than going to a top-nav
 *                pill, which took the guide out of the workspace entirely and
 *                made "collapse" feel like "disappear".
 * The X button — which only appears once all required steps are complete —
 * permanently dismisses the checklist in one click (calls
 * `completeChecklist()`, which flips `getStarted.completed`; the panel then
 * hides because `isVisible` reads that flag).
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Minimize2, ListChecks } from 'lucide-react';
import {
  Button,
  Progress,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  cn,
} from '@scio3/ui';
import { WistiaDialog } from '@/components/shared';
import { useOptibotStore } from '@/features/optibot/stores/optibot-store';
import { useGetStartedChecklist } from '../hooks/useGetStartedChecklist';
import { useGetStartedWidgetStore } from '../stores/get-started-widget-store';
import { GetStartedStep } from './GetStartedStep';
import { GETTING_STARTED_VIDEO_ID } from '../constants';

export function GetStartedChecklist() {
  const { t } = useTranslation();
  const { state, loading, completeChecklist } = useGetStartedChecklist();
  const widgetState = useGetStartedWidgetStore((s) => s.widgetState);
  const setWidgetState = useGetStartedWidgetStore((s) => s.setWidgetState);
  const pendingStepId = useGetStartedWidgetStore((s) => s.pendingStepId);
  const clearPendingStep = useGetStartedWidgetStore((s) => s.clearPendingStep);
  const isCollapsed = widgetState === 'collapsed';

  // OptiBot coordination. When OptiBot's panel is open it occupies the same
  // bottom-right corner, so we (a) publish our visibility so OptiBot forces
  // floating + shrinks (see optibot-store.checklistOpen / OptiBotPanel), and
  // (b) raise this panel to stack directly above OptiBot's shorter floating card.
  const optibotOpen = useOptibotStore((s) => s.isOpen);
  const setOptibotChecklistOpen = useOptibotStore((s) => s.setChecklistOpen);

  const [activeStepId, setActiveStepId] = useState<string | null>(() => {
    // Open the first incomplete step in display order — for a brand-new
    // account that's "Get Your Screen Set Up". Matches the restore-from-pill
    // logic below, which also picks the first incomplete step regardless of
    // whether it is required.
    const firstIncomplete = state.steps.find((s) => !s.completed);
    return firstIncomplete?.id ?? null;
  });
  const [showVideo, setShowVideo] = useState(false);
  // Publish our bottom-right visibility to OptiBot so it can force floating +
  // shrink while we are showing. Cleanup resets the flag on unmount (e.g. user
  // leaves the A/B arm or the checklist is dismissed) so OptiBot returns to the
  // user's chosen view mode. Declared before the early return so it always runs.
  useEffect(() => {
    setOptibotChecklistOpen(state.isVisible);
    return () => setOptibotChecklistOpen(false);
  }, [state.isVisible, setOptibotChecklistOpen]);

  // Apply a step-open REQUEST made from outside the panel. `activeStepId` stays local
  // state; the store field is a one-shot request we consume and clear, so the accordion
  // keeps exactly one owner. Declared before the early return below so it always runs.
  useEffect(() => {
    if (!pendingStepId) return;
    setActiveStepId(pendingStepId);
    clearPendingStep();
  }, [pendingStepId, clearPendingStep]);

  // Nothing to render once the checklist is dismissed/completed. There is no
  // exit animation to wait out any more — collapse keeps the widget mounted,
  // and dismiss is terminal.
  if (!state.isVisible) {
    return null;
  }

  const handleDismiss = async () => {
    // Permanently dismiss the checklist. Backend writes `getStarted.completed
    // = true` so this never re-appears for the user. No top-bar pill, no
    // re-entry — the ? help dropdown is the long-term home for resources now.
    await completeChecklist();
  };

  // The step to open when the collapsed circle is clicked. Prefers the first
  // incomplete REQUIRED step; once all required ones are done it points at a
  // remaining optional step.
  const nextStep =
    state.steps.find((step) => step.required && !step.completed) ??
    state.steps.find((step) => !step.completed);
  const stepsLeft = state.totalSteps - state.completedCount;

  const handleExpand = () => {
    setWidgetState('expanded');
    if (nextStep) setActiveStepId(nextStep.id);
  };

  const toggleStep = (stepId: string) => {
    setActiveStepId((current) => (current === stepId ? null : stepId));
  };

  // X (Dismiss guide) only becomes available once every required step is
  // done — until then the only affordance is collapsing it out of the way.
  // This is intentional: we don't want users to permanently kill the guide
  // before they've actually seen the steps through.
  const canDismiss = state.isComplete;

  return (
    <>
      {isCollapsed ? (
        // Collapsed: centred over the 58px FAB (right-6 + 3px = 27px) and stacked
        // above it at the same bottom-24 the panel rests at. Keeps the
        // `checklist-collapse-btn` test id + aria-expanded so the size toggle stays
        // one control to tests and assistive tech.
        <div
          className={cn(
            'fixed right-[27px] z-40 transition-[bottom] duration-300 ease-in-out',
            optibotOpen ? 'bottom-[452px]' : 'bottom-24',
          )}
          data-testid="get-started-checklist"
        >
          <TooltipProvider delayDuration={300}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  data-testid="checklist-collapse-btn"
                  variant="ghost"
                  onClick={handleExpand}
                  aria-expanded={false}
                  aria-label={t('getStarted.stepsLeft', { count: stepsLeft })}
                  className="relative h-13 w-13 cursor-pointer rounded-full bg-card p-0 text-primary shadow-[0_0_8px_1px_rgba(0,0,0,0.12)] transition-transform duration-150 hover:scale-105 hover:bg-card hover:text-primary dark:bg-muted dark:hover:bg-muted [&_svg]:size-6"
                >
                  <ListChecks />
                  {stepsLeft > 0 && (
                    <span
                      data-testid="checklist-steps-left"
                      className="absolute -right-0.5 -top-0.5 flex h-5 min-w-5 items-center justify-center rounded-full border-2 border-background bg-foreground px-1 text-xs font-semibold text-background"
                    >
                      {stepsLeft}
                    </span>
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent side="left">{t('getStarted.title')}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      ) : (
        <div
          className={cn(
            'fixed right-6 z-40 w-80 transition-[bottom] duration-300 ease-in-out',
            // While OptiBot's floating panel is open it sits in the same corner, so
            // raise this panel to stack above it. The raised offset (452px) =
            // OptiBot's bottom inset (16px) + its shortened height (420px,
            // FLOATING_HEIGHT_WITH_CHECKLIST_PX) + a 16px gap. Otherwise it rests at
            // its normal spot above the FAB. The max-height + scroll live on the
            // inner card (below) so the wrapper never clips its rounding/shadow.
            optibotOpen ? 'bottom-[452px]' : 'bottom-24',
          )}
          data-testid="get-started-checklist"
        >
          {/* Outer container: a gray frame around the white body card.
              When raised above OptiBot, cap the height and scroll WITHIN this card
              (not the wrapper) so a tall expanded checklist can't ride into the
              header on short viewports — and the card's own rounding + shadow stay
              intact (an element's box-shadow/border-radius aren't clipped by its
              own overflow). 516px = the 452px raised offset + ~4rem header
              headroom. */}
          <div
            className={cn(
              'rounded-lg bg-muted pt-1 shadow-[0_0_8px_1px_rgba(0,0,0,0.12)] dark:bg-panel-hover dark:shadow-none',
              optibotOpen && 'max-h-[calc(100dvh-516px)] overflow-y-auto thin-scrollbar',
            )}
          >
            {/* Header — sits directly on bg, no card. */}
            <div className="flex min-h-10 items-center gap-2 px-2 pb-1 pt-1">
              <span className="shrink-0 pl-1 text-sm font-semibold">{t('getStarted.title')}</span>
              {/* Progress bar — primary visual element in header */}
              <Progress
                value={state.progressPercent}
                className="h-2 flex-1 bg-muted-foreground/20"
              />
              {/* Right-side action cluster — collapse + X grouped with no gap so the
                  two icon buttons read as a single pair, separated from the
                  progress bar by the parent's gap-2. Collapse shrinks the panel to
                  the circle above the FAB (always available); X (Dismiss guide)
                  only appears once all required steps are complete. */}
              <div className="flex shrink-0 items-center">
                <Button
                  data-testid="checklist-collapse-btn"
                  variant="ghost"
                  size="icon-sm"
                  className="shrink-0 text-muted-foreground hover:text-foreground"
                  onClick={() => setWidgetState('collapsed')}
                  aria-label={t('getStarted.collapse')}
                  aria-expanded
                >
                  <Minimize2 className="h-4 w-4" />
                </Button>
                {canDismiss && (
                  <Button
                    data-testid="checklist-dismiss-btn"
                    variant="ghost"
                    size="icon-sm"
                    className="shrink-0 text-muted-foreground hover:text-foreground"
                    onClick={() => void handleDismiss()}
                    disabled={loading}
                    aria-label={t('Dismiss guide')}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>

            {/* Body — white card with border, steps + need help. */}
            <div className="rounded-lg border border-border bg-card p-3 dark:bg-muted">
              {/* Steps list */}
              <div className="space-y-1">
                {state.steps.map((step) => (
                  <GetStartedStep
                    key={step.id}
                    step={step}
                    isActive={activeStepId === step.id}
                    onToggle={() => toggleStep(step.id)}
                  />
                ))}
              </div>

              {/* Need help section */}
              <div className="mt-3 flex flex-wrap items-center gap-1 px-2 text-xs select-none">
                <span className="text-muted-foreground">{t('getStarted.needHelp')}</span>
                <Button
                  data-testid="checklist-watch-video-btn"
                  variant="link"
                  size="sm"
                  className="h-auto p-0 text-xs underline"
                  onClick={() => setShowVideo(true)}
                >
                  {t('getStarted.watchVideo')}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      <WistiaDialog
        videoId={showVideo ? GETTING_STARTED_VIDEO_ID : null}
        onClose={() => setShowVideo(false)}
      />
    </>
  );
}
